import type { ReactNode } from "react";
import type { DiscoverGameGameTeamResponse, DiscoverGameV2Response } from "libs/apiClient";
import { Link } from "react-router";
import { _ } from "i18n";
import { ContentCell } from "../../ContentCell";
import { DISCOVER_GAME_V2_DETAIL_QUERY_KEY } from "./constants";
import { GamePlayersTable } from "../../GamePlayersTable";

type TeamSide = "home" | "away";

type TeamFieldConfig = {
	key: string;
	title: string;
	type: "string" | "link";
	value?: string | null;
	formatter?: () => ReactNode;
};

function buildTeamFields(team: DiscoverGameGameTeamResponse, side: TeamSide): TeamFieldConfig[] {
	if (side === "home") {
		return [
			{
				key: "home-clubName",
				title: _("discover.gameDetail.field.homeGameClubName"),
				type: "string",
				value: team.clubName ?? "-",
			},
			{
				key: "home-teamName",
				title: _("discover.gameDetail.field.homeGameTeamName"),
				type: "string",
				value: team.teamName ?? "-",
			},
			{
				key: "home-club",
				title: _("discover.gameDetail.field.homeClub"),
				type: "link",
				formatter: () =>
					team.club
						? <Link to={`/discover/club/${team.club.id}`} className="button-link">{team.club.name}</Link>
						: "-",
			},
			{
				key: "home-team",
				title: _("discover.gameDetail.field.homeTeamLink"),
				type: "link",
				formatter: () =>
					team.team
						? <Link to={`/discover/team/${team.team.id}`} className="button-link">{team.team.name}</Link>
						: "-",
			},
			{
				key: "home-shirt",
				title: _("discover.gameDetail.field.homeShirtColor"),
				type: "string",
				value: team.colorShirt ?? "-",
			},
			{
				key: "home-short",
				title: _("discover.gameDetail.field.homeShortColor"),
				type: "string",
				value: team.colorShort ?? "-",
			},
		];
	}

	return [
		{
			key: "away-clubName",
			title: _("discover.gameDetail.field.awayGameClubName"),
			type: "string",
			value: team.clubName ?? "-",
		},
		{
			key: "away-teamName",
			title: _("discover.gameDetail.field.awayGameTeamName"),
			type: "string",
			value: team.teamName ?? "-",
		},
		{
			key: "away-club",
			title: _("discover.gameDetail.field.awayClub"),
			type: "link",
			formatter: () =>
				team.club
					? <Link to={`/discover/club/${team.club.id}`} className="button-link">{team.club.name}</Link>
					: "-",
		},
		{
			key: "away-team",
			title: _("discover.gameDetail.field.awayTeamLink"),
			type: "link",
			formatter: () =>
				team.team
					? <Link to={`/discover/team/${team.team.id}`} className="button-link">{team.team.name}</Link>
					: "-",
		},
		{
			key: "away-shirt",
			title: _("discover.gameDetail.field.awayShirtColor"),
			type: "string",
			value: team.colorShirt ?? "-",
		},
		{
			key: "away-short",
			title: _("discover.gameDetail.field.awayShortColor"),
			type: "string",
			value: team.colorShort ?? "-",
		},
	];
}

function TeamCard({
	titleKey,
	team,
	side,
}: {
	titleKey: "discover.gameDetail.field.homeTeam" | "discover.gameDetail.field.awayTeam";
	team: DiscoverGameGameTeamResponse;
	side: TeamSide;
}) {
	const fields = buildTeamFields(team, side);

	return (
		<div className="discover-column">
			<div className="discover-card">
				<div className="discoverGameDetailV2-teamsheetCardTitle">{_(titleKey)}</div>
				<div className="cell-grid cell-grid--2">
					{fields.map((props) => (
						<ContentCell
							key={props.key}
							invalidationKey={DISCOVER_GAME_V2_DETAIL_QUERY_KEY}
							title={props.title}
							type={props.type}
							value={props.value}
							formatter={props.formatter}
						/>
					))}
				</div>
				<GamePlayersTable gamePlayersData={team.gamePlayers ?? []} />
			</div>
		</div>
	);
}

interface DiscoverGameTeamsheetTabProps {
	gameData: DiscoverGameV2Response;
}

export function DiscoverGameTeamsheetTab({ gameData }: DiscoverGameTeamsheetTabProps) {
	return (
		<div className="discover-columns discoverGameDetailV2-teamsheetColumns">
			<TeamCard titleKey="discover.gameDetail.field.homeTeam" team={gameData.home} side="home" />
			<TeamCard titleKey="discover.gameDetail.field.awayTeam" team={gameData.away} side="away" />
		</div>
	);
}
